import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../supabase/supabase';

export default function Admin() {
  const { user, addNotification } = useApp();
  const [activeTab, setActiveTab] = useState('rewards');
  const [users, setUsers] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user.isAdmin) {
      fetchUsers();
      fetchWinners();
    }
  }, [user.isAdmin]);

  const fetchUsers = async () => {
    const { data } = await supabase.from('profiles_v3').select('*').order('monthly_xp', { ascending: false });
    if (data) setUsers(data);
  };

  const fetchWinners = async () => {
    const { data } = await supabase.from('monthly_winners_v1').select('*').order('created_at', { ascending: false });
    if (data) setWinners(data);
  };

  const finalizeMonth = async () => {
    if (users.length < 3) {
      addNotification("Need at least 3 participants to award a prize.", "error");
      return;
    }

    const winner = users[0];
    const monthYear = new Date().toLocaleString('default', { month: '2-digit', year: 'numeric' }).replace('/', '-');

    setLoading(true);
    try {
      // 1. Record Winner
      const { error: winErr } = await supabase.from('monthly_winners_v1').insert([{
        user_id: winner.id,
        month_year: monthYear,
        xp_earned: winner.monthly_xp
      }]);
      if (winErr) throw winErr;

      // 2. Reset Everyone's Monthly XP
      const { error: resetErr } = await supabase.from('profiles_v3').update({ monthly_xp: 0 }).neq('id', '00000000-0000-0000-0000-000000000000');
      if (resetErr) throw resetErr;

      addNotification(`Month closed! Winner: ${winner.email}`, "success");
      fetchUsers();
      fetchWinners();
    } catch (err) {
      addNotification(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!user.isAdmin) return null;

  return (
    <div className="p-4 pb-24 md:pb-8 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center tracking-tight">
          <SafeIcon icon={FiIcons.FiShield} className="mr-2 text-blue-500" /> Admin Command
        </h2>
        <div className="flex bg-slate-850 p-1 rounded-lg border border-slate-800">
          {['rewards', 'users'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${activeTab === tab ? 'bg-blue-600 text-white' : 'text-slate-500'}`}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'rewards' && (
        <div className="space-y-6">
          <div className="bg-slate-850 border border-slate-800 rounded-2xl p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-bold text-white">Monthly Prize Management</h3>
                <p className="text-xs text-slate-500">Close the current month and pick the top traveler.</p>
              </div>
              <button 
                onClick={finalizeMonth}
                disabled={loading}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 px-6 py-2 rounded-xl font-bold text-xs transition-all shadow-lg"
              >
                Close Month & Pick Winner
              </button>
            </div>
            
            <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
              <div className="flex justify-between text-[10px] font-black text-slate-500 uppercase mb-4">
                <span>Current Month Standings</span>
                <span>{users.length} Active Participants</span>
              </div>
              <div className="space-y-2">
                {users.slice(0, 3).map((u, i) => (
                  <div key={u.id} className="flex justify-between items-center p-2 bg-slate-800/50 rounded-lg">
                    <span className="text-xs font-bold text-white">{i + 1}. {u.email}</span>
                    <span className="text-xs font-mono text-amber-500 font-bold">{u.monthly_xp} XP</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-slate-850 border border-slate-800 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-slate-800 bg-slate-900/50">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Winner History</h3>
            </div>
            <table className="w-full text-left">
              <thead className="bg-slate-900 text-[10px] text-slate-500 uppercase">
                <tr>
                  <th className="p-4">Month</th>
                  <th className="p-4">Winner</th>
                  <th className="p-4">Choice</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {winners.map(w => (
                  <tr key={w.id} className="text-xs">
                    <td className="p-4 font-mono">{w.month_year}</td>
                    <td className="p-4 text-slate-300">User ID: {w.user_id.slice(0, 8)}...</td>
                    <td className="p-4"><span className="bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded font-bold uppercase text-[9px]">{w.reward_choice || 'Pending'}</span></td>
                    <td className="p-4">
                      {w.is_delivered ? <span className="text-emerald-500">Delivered</span> : <button className="text-amber-500 underline">Mark Sent</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-slate-850 border border-slate-800 rounded-2xl overflow-hidden">
          {/* ... existing users table ... */}
        </div>
      )}
    </div>
  );
}